import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Typography, Divider, IconButton, styled,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { FormTextArea } from "@/components/custom/custom-form-textarea";
import { formatCurrency } from "@/utils/format-currency";
import { toIsoDateTime } from "@/utils/format-date";
import { mapTransactionToDisputeContext } from "@/utils/map-transaction-to-dispute";
import { showToast } from "@/redux/actions/toast-actions";
import { createDisputeSchema } from "@/schemas/dispute.schema";
import { useCreateDispute } from "@/hooks/use-create-dispute";
import DisputeTransactionSearch from "./dispute-transaction-search";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": { borderRadius: "12px", padding: theme.spacing(2), maxWidth: 480, width: "100%" },
}));

const ReadOnlyRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value ?? "N/A"}</Typography>
  </Stack>
);

// `presetTransaction` = raw transaction object, already known (transaction
// page → nav handoff). When absent, the dialog opens in search mode so the
// admin can find one by reference/ID first — same form either way once a
// transaction is in hand.
const CreateDisputeDialog = ({ open, presetTransaction, onClose }) => {
  const dispatch = useDispatch();
  const mutation = useCreateDispute();

  const [selectedTx, setSelectedTx] = useState(presetTransaction ?? null);

  useEffect(() => {
    if (open) setSelectedTx(presetTransaction ?? null);
  }, [open, presetTransaction]);

  const context = useMemo(() => mapTransactionToDisputeContext(selectedTx), [selectedTx]);
  const step = context ? "form" : "search";

  const formik = useFormik({
    initialValues: { disputeType: "", reasonForDisputeInDetails: "" },
    validationSchema: createDisputeSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate(
        {
          customerId: context?.customerId,
          merchantId: context?.merchantId,
          transactionAmount: context?.transactionAmount,
          transactionDate: toIsoDateTime(context?.transactionDate),
          transactionReference: context?.transactionReference,
          disputeType: values.disputeType,
          reasonForDisputeInDetails: values.reasonForDisputeInDetails,
        },
        {
          onSuccess: (data) => {
            dispatch(showToast(data?.message ?? "Dispute created", "success"));
            handleFullClose();
          },
          onError: (err) =>
            dispatch(showToast(err?.message ?? "Failed to create dispute", "error")),
        },
      );
    },
  });

  useEffect(() => {
    if (step === "form") {
      formik.resetForm();
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedTx]);

  const handleFullClose = () => {
    setSelectedTx(null);
    onClose();
  };

  const handleClose = () => {
    if (mutation.isPending) return;
    handleFullClose();
  };

  // Only offered when the admin arrived via search — a preset transaction
  // (from the transaction page) has nowhere else to go back to.
  const handleBackToSearch = () => setSelectedTx(null);

  return (
    <StyledDialog open={open} onClose={handleClose}>
      {step === "search" ? (
        <DisputeTransactionSearch onSelect={(tx) => setSelectedTx(tx)} onCancel={handleClose} />
      ) : (
        <>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              {!presetTransaction && (
                <IconButton size="small" onClick={handleBackToSearch} disabled={mutation.isPending}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              )}
              <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
                Create Dispute
              </DialogTitle>
            </Stack>

            {mutation.isError && (
              <CustomErrorMessage message={mutation.error?.message ?? "Failed to create dispute"} />
            )}

            <Stack
              spacing={1}
              sx={{ p: 1.5, borderRadius: 2, backgroundColor: "grey.50", border: "1px solid", borderColor: "divider" }}
            >
              <ReadOnlyRow label="Customer" value={context?.customerName} />
              <ReadOnlyRow label="Merchant ID" value={context?.merchantId} />
              <ReadOnlyRow label="Transaction Ref" value={context?.transactionReference} />
              <ReadOnlyRow label="Amount" value={formatCurrency(context?.transactionAmount)} />
            </Stack>

            <Divider />

            <FormSelectInput
              formik={formik}
              name="disputeType"
              label="Dispute Type"
              options={[
                { label: "Auth Complaint", value: "AUTHCOMPLAINT" },
                {label: "Chargeback", value: "CHARGEBACK"},
                { label: "Fraud", value: "FRAUD" },
                { label: "Others", value: "OTHERS" },
              ]}
            />
            <FormTextArea
              formik={formik}
              name="reasonForDisputeInDetails"
              label="Reason for Dispute"
              rows={4}
            />
          </DialogContent>

          <DialogActions sx={{ px: 2, pt: 1, pb: 1, gap: 1 }}>
            <CustomButton variantType="outline" size="small" onClick={handleClose} disabled={mutation.isPending} sx={{ px: 2 }}>
              Cancel
            </CustomButton>
            <CustomButton
              size="small"
              disabled={!formik.isValid || mutation.isPending}
              isLoading={mutation.isPending}
              onClick={formik.handleSubmit}
              sx={{ px: 2 }}
            >
              {mutation.isPending ? "Creating..." : "Create Dispute"}
            </CustomButton>
          </DialogActions>
        </>
      )}
    </StyledDialog>
  );
};

export default CreateDisputeDialog;